import {
    Divider,
    Grid,
    Table,
    TableBody,
    TableHead,
    Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SubmitButton } from '@tassat/button';
import {
    AccountRecord,
    AccountWithdrawalDataRecord,
    Asset,
    Transaction
} from '@tassat/fred-definitions';
import { formatAsAsset } from '@tassat/utility';
import React from 'react';

import { registerTimeout } from 'Lib/serverTimeout';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { withdrawalTimeoutAction } from 'Reducers/actions/validationActions';
import { useAppDispatch } from 'Reducers/store/hooks';
import WithdrawalInstructionDetails from 'SharedComponents/withdrawalInstructions/withdrawalInstructionDetails';

import { IWithdrawalFormValues } from '../overlays/withdrawal/withdrawalForm/types';

interface IWithdrawalConfirmationProps {
    withdrawalValues: IWithdrawalFormValues | Transaction;
    withdrawalInstruction?: AccountWithdrawalDataRecord;
    currentWallet: AccountRecord;
    handleWithdrawal: (withdrawalValues) => void;
    cancelConfirmation: () => void;
    dxyObject: Asset;
    asset: Asset;
}

const muiStyles = makeStyles((theme: ApplicationMuiTheme) => ({
    root: { padding: '0 5rem' },
    contentDivider: {
        marginTop: '.5rem',
        marginBottom: '.3rem'
    },
    amountText: {
        padding: '.5rem',
        paddingBottom: '1.5rem'
    },
    disclaimerContainer: { paddingTop: '1.5rem' },
    disclaimerText: { flexBasis: '33%' },
    paymentDetailsContainer: {},
    table: {
        borderCollapse: 'separate' as const,
        borderSpacing: '.5rem',
        wordBreak: 'break-all' as const
    },
    tableRow: { marginBottom: '.3rem' },
    tableLabelCell: {
        backgroundColor: theme?.app?.applicationColors.tableRowLabelColor ||
            'rgba(185, 217, 236,5)',
        fontWeight: 'bold' as const,
        fontSize: '.875rem'
    },
    tableCell: {
        border: 'none' as const,
        paddingTop: '.3rem',
        paddingBottom: '.3rem'
    },
    submitContainer: { paddingTop: '2.5rem' },
    cancelButton: {
        cursor: 'pointer' as const,
        fontSize: '1.2rem',
        color: theme?.app?.clientTheme.colors.primaryColor ||
            '#375073',
        padding: '.3rem',
        marginTop: '1rem',
        '&:hover': {
            backgroundColor: theme?.app?.clientTheme.colors.buttons.primaryHover ||
                '#bec4cf'
        }
    },
    beneficiaryInfoContainer: {
        margin: 0,
        padding: 0
    }
}));

export function WithdrawalConfirmation(props: IWithdrawalConfirmationProps) {
    const {
        withdrawalValues,
        handleWithdrawal,
        cancelConfirmation,
        dxyObject,
        withdrawalInstruction,
        asset
    } = props;

    const classes = muiStyles(props);
    const dispatch = useAppDispatch();
    const onTimeoutError = () => dispatch(withdrawalTimeoutAction);

    const fromApproval = (withdrawalValues as Transaction).RequestId;

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid item>
                <Typography variant='body1'>You are about to withdraw</Typography>
                <Typography
                    className={classes.amountText}
                    variant='h3'
                >
                    {fromApproval ?
                        formatAsAsset((withdrawalValues as Transaction).AssetQuantity, asset, 'symbol')
                        : formatAsAsset((withdrawalValues as IWithdrawalFormValues).WithdrawalAmount, dxyObject, 'symbol')}
                </Typography>
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                container
                direction='row'
                item
                spacing={0}
            >
                <Grid
                    className={classes.paymentDetailsContainer}
                    container
                    direction='column'
                    item
                    lg={6}
                    md={12}
                    spacing={0}
                    wrap='nowrap'
                >
                    <Table className={classes.table}>
                        <TableHead />
                        <TableBody>
                            <WithdrawalInstructionDetails
                                displayComponent='Table'
                                withdrawalInstruction={
                                    !fromApproval ?
                                        (withdrawalValues as IWithdrawalFormValues).WithdrawalInstructions.refData
                                        : withdrawalInstruction
                                }
                            />
                        </TableBody>
                    </Table>
                </Grid>
                <Grid
                    className={classes.disclaimerContainer}
                    container
                    direction='column'
                    item
                    lg={6}
                    md={12}
                    spacing={0}
                    wrap='nowrap'
                >
                    <Typography
                        className={classes.disclaimerText}
                        variant='body1'
                    >
                        Please review the details of your withdrawal request.
                    </Typography>
                    <Typography
                        className={classes.disclaimerText}
                        variant='body1'
                    >
                        Once you confirm this request, the requested amount of cash to be withdrawn will no longer
                        be available in your brokerage account and will be sent to the external banking account designated by you.
                    </Typography>
                    <Typography
                        className={classes.disclaimerText}
                        variant='body1'
                    >
                        {'Please refer to the funds availability disclosure in '}
                        <a
                            href='https://tzero.com/disclosure-library/'
                            rel='noreferrer'
                            target='_blank'
                        >
                            tZERO Securities customer agreement
                        </a>
                        {' for more information.'}
                    </Typography>
                </Grid>
            </Grid>
            <Grid
                className={classes.contentDivider}
                component={Divider}
                item
            />
            <Grid
                alignItems='center'
                className={classes.submitContainer}
                container
                direction='column'
                item
                spacing={0}
            >
                <SubmitButton
                    disabled={!fromApproval &&
                        (withdrawalInstruction === undefined || withdrawalInstruction?.IsDeleted)}
                    onClick={() => {
                        handleWithdrawal(withdrawalValues);
                        registerTimeout(onTimeoutError, 'withdrawal');
                    }}
                    type={fromApproval ? 'withdraw-approve' : 'withdraw-confirm'}
                />
                <Grid item>
                    <Typography
                        className={classes.cancelButton}
                        onClick={cancelConfirmation}
                        variant='h4'
                    >CANCEL
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default WithdrawalConfirmation;